<template>
  <div class="sales-leave-table" :class="{ 'sales-leave-table--grouped': grouped }">
    <app-data-table design="new" :columns="columns" :rows="items" :loading="loading" :density="workspace ? 'dense' : 'compact'" row-key="id" :min-width="workspace ? '1200px' : '980px'">
      <template #cell-employee="{ row }">
        <app-table-user v-if="workspace" :name="row.employee.full_name" :photo="row.employee.photo || ''"><router-link class="ui-link" :to="{ name: 'employees-profile', params: { id: row.employee.id } }">{{ row.employee.full_name }}</router-link></app-table-user>
        <router-link v-else-if="showEmployee" class="sales-leave-table__employee" :to="{ name: 'employees-profile', params: { id: row.employee.id } }">{{ row.employee.full_name }}</router-link>
        <strong v-else>{{ row.employee.full_name }}</strong>
      </template>
      <template #cell-request="{ row }">
        <app-status-badge design="new" compact :label="kindLabel(row.kind)" :tone="row.kind === 'change_dates' ? 'amber' : 'green'" />
        <span v-if="!grouped" class="sales-leave-table__muted">{{ formatDateTime(row.created_at) }}</span>
      </template>
      <template #cell-type="{ row }">{{ row.type.name }}</template>
      <template #cell-period="{ row }">
        <template v-if="workspace">
          <div v-if="row.kind === 'change_dates'"><app-table-text :text="`Was ${formatPeriod(row.old_from_date, row.old_to_date)} (${row.old_days} d)`" /></div>
          <div><app-table-text :text="`${row.kind === 'change_dates' ? 'Now ' : ''}${formatPeriod(row.from_date, row.to_date)}`" /></div>
          <div v-if="warning(row)"><app-table-text :text="`Over the ${row.annual.limit}-day yearly reference: ${warning(row)}`" /></div>
        </template>
        <template v-else>
        <template v-if="row.kind === 'change_dates'">
          <span class="sales-leave-table__muted">Was {{ formatPeriod(row.old_from_date, row.old_to_date) }} ({{ row.old_days }} d)</span>
          <span>Now {{ formatPeriod(row.from_date, row.to_date) }}</span>
        </template>
        <span v-else>{{ formatPeriod(row.from_date, row.to_date) }}</span>
        <span v-if="warning(row)" class="sales-leave-table__warning">
          Over the {{ row.annual.limit }}-day yearly reference: {{ warning(row) }}
        </span>
        </template>
      </template>
      <template #cell-days="{ row }">{{ row.days }}</template>
      <template #cell-status="{ row }">
        <app-status-badge design="new" compact :label="row.status.name" :tone="statusTone(row)" :color="grouped ? '' : row.status.color" />
      </template>
      <template #cell-reason="{ row }">
        <app-table-text v-if="workspace" :text="row.reason" />
        <span v-else-if="row.reason" v-tooltip="{ content: row.reason, placement: 'top-start', offset: 10 }" class="sales-leave-table__reason" tabindex="0">{{ row.reason }}</span>
        <span v-else>—</span>
      </template>
      <template #cell-submitted="{ row }">
        <app-table-text v-if="workspace" :text="relativeDate(row.created_at)" :tooltip="row.created_at ? formatDateTime(row.created_at) : ''" />
        <span v-else v-tooltip="{ content: formatDateTime(row.created_at), placement: 'top-start', offset: 10 }" tabindex="0">{{ relativeDate(row.created_at) }}</span>
      </template>
      <template #cell-decision="{ row }">
        <template v-if="row.reviewed_by">
          <app-table-user v-if="workspace" :name="row.reviewed_by.full_name" :photo="row.reviewed_by.photo || ''" />
          <template v-else>{{ row.reviewed_by.full_name }}</template>
          <span class="sales-leave-table__muted">{{ formatDateTime(row.reviewed_at) }}</span>
        </template>
        <app-table-text v-if="workspace && row.decision_reason" :text="row.decision_reason" /><span v-else-if="row.decision_reason">{{ row.decision_reason }}</span>
        <span v-if="row.released_periods.length" class="sales-leave-table__warning">
          Released days need re-planning:
          <span v-for="period in row.released_periods" :key="period.from_date">
            {{ formatPeriod(period.from_date, period.to_date) }}
          </span>
        </span>
        <span v-if="!row.reviewed_by && !row.decision_reason">—</span>
      </template>
      <template #cell-actions="{ row }">
        <div v-if="workspace" class="ui-actions ui-actions--tight">
          <app-icon-button v-if="can(row, 'approve') || can(row, 'reject')" label="Preview in schedule" @click="$emit('preview', row)"><i class="pi pi-calendar" aria-hidden="true" /></app-icon-button>
          <app-icon-button v-if="can(row, 'approve')" label="Approve" @click="$emit('approve', row)"><i class="pi pi-check" aria-hidden="true" /></app-icon-button>
          <app-icon-button v-if="can(row, 'reject')" label="Reject" @click="$emit('reject', row)"><i class="pi pi-times" aria-hidden="true" /></app-icon-button>
          <app-icon-button v-if="can(row, 'cancel')" label="Cancel" @click="$emit('cancel', row)"><i class="pi pi-ban" aria-hidden="true" /></app-icon-button>
        </div>
        <div v-else class="sales-leave-table__actions">
          <button v-if="can(row, 'approve') || can(row, 'reject')" class="sales-leave-table__calendar-button" type="button" aria-label="Preview in schedule" title="Preview in schedule" @click="$emit('preview', row)">
            <img src="@/assets/img/calendar-picker-icon.svg" alt="">
          </button>
          <app-button v-if="can(row, 'approve')" design="new" theme="success" size="small" @click.native="$emit('approve', row)">Approve</app-button>
          <app-button v-if="can(row, 'reject')" design="new" theme="error" size="small" @click.native="$emit('reject', row)">Reject</app-button>
          <app-button v-if="can(row, 'cancel')" design="new" theme="dark" size="small" @click.native="$emit('cancel', row)">Cancel</app-button>
        </div>
      </template>
      <template #empty>
        <app-no-results design="new" small title="No requests" text="Try another status or filter." :margin-top="0" :margin-bottom="0" />
      </template>
    </app-data-table>
  </div>
</template>

<script>
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import { REQUEST_KIND_LABELS, annualWarning } from './salesLeave'
import { formatDateTime, formatPeriod } from './format'
import { parseISO } from 'date-fns'

export default {
  components: { AppTableText, AppTableUser },
  name: 'SalesLeaveRequestsTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    showEmployee: {
      type: Boolean,
      default: false
    },
    grouped: {
      type: Boolean,
      default: false
    },
    filterStatus: {
      type: String,
      default: null
    },
    workspace: Boolean,
    loading: Boolean
  },
  computed: {
    columns() {
      return [
        ...(this.showEmployee ? [{ key: 'employee', label: 'Employee', minWidth: '160px' }] : []),
        { key: 'request', label: 'Request', minWidth: '110px' },
        ...(!this.grouped ? [{ key: 'type', label: 'Type', minWidth: '130px' }] : []),
        { key: 'period', label: 'Requested period', minWidth: '190px' },
        { key: 'days', label: 'Days', align: 'right', width: '70px' },
        ...(this.filterStatus === null || this.filterStatus === '' ? [{ key: 'status', label: 'Status', width: '110px' }] : []),
        { key: 'reason', label: 'Reason', minWidth: '150px' },
        ...(this.grouped ? [{ key: 'submitted', label: 'Requested at', minWidth: '100px' }] : []),
        ...(this.filterStatus !== 'requested' ? [{ key: 'decision', label: 'Decision', minWidth: '150px' }] : []),
        ...(this.filterStatus === null || this.filterStatus === '' || this.filterStatus === 'requested' ? [{ key: 'actions', label: 'Actions', width: '210px' }] : [])
      ]
    }
  },
  methods: {
    formatDateTime,
    formatPeriod,
    relativeDate(date) {
      if (!date) return '—'
      const parsed = parseISO(date)
      if (Number.isNaN(parsed.getTime())) return '—'
      const seconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000))
      if (seconds < 60) return `${seconds}s`
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
      return `${Math.floor(seconds / 86400)}d`
    },
    kindLabel: kind => REQUEST_KIND_LABELS[kind] || kind,
    statusTone(row) {
      return { requested: 'amber', approved: 'green', rejected: 'red', cancelled: 'neutral' }[row.status.code] || 'neutral'
    },
    can: (row, action) => row.available_actions.includes(action),
    warning: row => (row.status.code === 'requested' ? annualWarning(row.annual) : '')
  }
}
</script>

<style lang="scss" scoped>
@import './salesLeaveTable';
</style>
